import { MprcInfiniteLoading, MprcRequestErrorReport } from "components";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { GlobalSearchDto, GlobalSearchOrderBy, KnowledgeSearchFilters, KnowledgeSearchOption, TrainingSearchFilters, TrainingSearchRestriction } from "generated/CoreApi_v1";
import { INSIDE_LAYOUT_CONTENT_CONTAINER_ID } from "layouts/common/inside/MprcLayoutInside";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { GlobalSearchRowsPerPage, SearchTab, TabOptions } from "views/general/GlobalSearch/components/GlobalSearchTabs";
import { GlobalSearchFirstChars } from "views/general/GlobalSearch/HeaderSearch";

/////////////////////////////////////////////////////////////////////////////////////////////////

type GlobalSearchSaveState = {
  selectedTab: SearchTab;
  pageNumber: number;
  scrollY: number;
  orderBy: GlobalSearchOrderBy;
  kFilters: KnowledgeSearchFilters;
  vFilters: KnowledgeSearchFilters;
  dFilters: KnowledgeSearchFilters;
  tFilters: TrainingSearchFilters;
};

type GlobalSearchContextType = GlobalSearchSaveState & {
  searchResult: GlobalSearchDto | null;
  globalSearchText: string;
  tabOptions: TabOptions;
  loadingResume: boolean;
  isDatasetEmpty: boolean;
  anyTabWithData: boolean;
  setPageNumber: (page: number) => void;
  setScrollY: (y: number) => void;

  filterOpened: boolean;
  setFilterOpened: (opened: boolean) => void;

  loading: boolean;
  setLoading: (loading: boolean) => void;

  orderBy: GlobalSearchOrderBy;
  setOrderBy: (orderBy: GlobalSearchOrderBy) => void;

  errors: any;
  setErrors: (errors: any) => void;

  kFilters: KnowledgeSearchFilters;
  setKnowledgeFilters: (filters: Partial<KnowledgeSearchFilters>) => void;

  vFilters: KnowledgeSearchFilters;
  setVideoFilters: (filters: Partial<KnowledgeSearchFilters>) => void;

  dFilters: KnowledgeSearchFilters;
  setDocumentFilters: (filters: Partial<KnowledgeSearchFilters>) => void;

  tFilters: TrainingSearchFilters;
  setTrainingFilters: (filters: Partial<TrainingSearchFilters>) => void;
};

/////////////////////////////////////////////////////////////////////////////////////////////////

const emptyKnowledgeFilter = (searchOption: KnowledgeSearchOption) => {
  return {
    attachDocumentType: null,
    classificationType: null,
    period: null,
    periodEnd: null,
    periodStart: null,
    publicationType: null,
    restriction: null,
    videoDuration: null,
    searchOption
  } as KnowledgeSearchFilters;
};

const emptyTrainingFilter = () => {
  return {
    duration: null,
    publication: null,
    publicationEnd: null,
    publicationStart: null,
    restriction: TrainingSearchRestriction.All,
    start: null,
    trainingContentType: null,
    trainingEnd: null,
    trainingStart: null
  };
};

/////////////////////////////////////////////////////////////////////////////////////////////////

const GlobalSearchContext = React.createContext<GlobalSearchContextType>(undefined!);

export const useGlobalSearch = () => React.useContext(GlobalSearchContext);

/////////////////////////////////////////////////////////////////////////////////////////////////

export const GlobalSearchProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const api = useCoreApi();
  const location = useLocation();
  const navigate = useNavigate();

  const { tab } = useParams();
  const { t } = useTranslation("shared");

  const [searchParams] = useSearchParams();
  const [globalSearchText, changeSearchText] = useState<string>("");
  const [firstChar, changeFirstChar] = useState<string>("");
  const [selectedTab, changeTab] = useState<SearchTab>((tab as SearchTab) ?? "All");
  const [lastError, setLastError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingResume, setLoadingResume] = useState<boolean>(true);
  const [filterOpened, setFilterOpened] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<GlobalSearchDto | null>(null);
  const [tabOptions, setTabOptions] = useState<TabOptions>({ hasAll: false, hasDocuments: false, hasTrainings: false, hasVideos: false });

  const hasData = useRef<boolean>(false);
  const hasTabData = useRef<boolean>(false);

  ///
  async function loadResume() {
    setLoadingResume(true);
    setLastError(null);
    setSearchResult(null);
    resetTabOptions();

    api.v1.globalSearch.globalSearch({ searchText: globalSearchText, pageNumber: 1, pageSize: GlobalSearchRowsPerPage })
      .then((results) => {

        const objectResults = [
          results.data.knowledgeSearch.knowledgeSearchResult,
          results.data.channel,
          results.data.topic,
          results.data.user,
          results.data.training.traningSearchResult,
        ];

        const newTabOptions = {
          hasAll: objectResults.filter(p => p.totalRows > 0).length > 0,
          hasDocuments: results.data.knowledgeDocs.knowledgeSearchResult.totalRows > 0,
          hasVideos: results.data.knowledgeVideos.knowledgeSearchResult.totalRows > 0,
          hasTrainings: results.data.training.traningSearchResult.totalRows > 0 || firstChar == "~"
        };

        hasTabData.current = newTabOptions.hasAll
          || newTabOptions.hasTrainings
          || newTabOptions.hasDocuments
          || newTabOptions.hasVideos;

        hasData.current = hasTabData.current
          || results.data.channel.totalRows > 0
          || results.data.topic.totalRows > 0
          || results.data.user.totalRows > 0
          || results.data.training.traningSearchResult.totalRows > 0
          ;

        handleChangeTabOptions(newTabOptions);
        setSearchResult(results.data);
      })
      .catch(e => {
        setLastError(e);
      })
      .finally(() => {
        setLoadingResume(false);
      });
  }

  ///
  const handleChangeTabOptions = (options: TabOptions) => {
    setTabOptions(options);

    if (
      (selectedTab == "Documents" && !options.hasDocuments)
      ||
      (selectedTab == "Videos" && !options.hasVideos)
      ||
      (selectedTab == "Trainings" && !options.hasTrainings)
    ) {
      changeTab("All");
    }
    else if (selectedTab == "All" && !options.hasAll) {
      if (options.hasTrainings) changeTab("Trainings");
      else if (options.hasVideos) changeTab("Videos");
      else if (options.hasDocuments) changeTab("Documents");
    }
  };

  ///
  const resetTabOptions = () => {
    hasData.current = false;
    setTabOptions({ hasAll: false, hasDocuments: false, hasTrainings: false, hasVideos: false });
  };

  ///
  const changeState = (newState: Partial<GlobalSearchSaveState>) => {
    navigate(document.location.search, { replace: false, state: { ...state, ...newState } });
  };

  ///
  const state = useMemo(() => {
    const s =
      location.state
      ?? {
        pageNumber: 1,
        scrollY: document.getElementById(INSIDE_LAYOUT_CONTENT_CONTAINER_ID)?.scrollTop ?? 0,
        orderBy: GlobalSearchOrderBy.Relevance,
        kFilters: emptyKnowledgeFilter(KnowledgeSearchOption.All),
        vFilters: emptyKnowledgeFilter(KnowledgeSearchOption.Videos),
        dFilters: emptyKnowledgeFilter(KnowledgeSearchOption.Documents),
        tFilters: emptyTrainingFilter()
      } as GlobalSearchSaveState;

    return {
      pageNumber: s.pageNumber,
      scrollY: s.scrollY,
      orderBy: s.orderBy,
      kFilters: s.kFilters,
      vFilters: s.vFilters,
      dFilters: s.dFilters,
      tFilters: s.tFilters,
    } as GlobalSearchSaveState;
  }, [location.state]);


  ///
  const getSearchText = (text: string): { text: string, firstChar?: string; } => {
    const firstChar = text[0];
    if (GlobalSearchFirstChars.indexOf(firstChar) > -1)
      return { text: text.substring(1), firstChar };
    return { text: text.trim() };
  };

  ///
  useEffect(() => {
    const q = searchParams.get("q");
    const newGlobalSearchText = getSearchText(q == null ? "" : decodeURIComponent(q));
    changeSearchText(newGlobalSearchText.text);
    changeFirstChar(newGlobalSearchText.firstChar ?? "");
  }, [document.location.search]);

  ///
  useEffect(() => {
    const newTab = (tab as SearchTab) ?? "All";
    changeTab(newTab);
    setFilterOpened(false);
  }, [tab]);

  ///
  useEffect(() => {
    if (globalSearchText != "") loadResume();
  }, [globalSearchText]);

  return (
    <GlobalSearchContext.Provider
      value={
        {
          globalSearchText,
          selectedTab,
          tabOptions,
          anyTabWithData: hasTabData.current,
          isDatasetEmpty: !hasData.current,
          loadingResume,
          pageNumber: state.pageNumber,
          setPageNumber: (pageNumber: number) => changeState({ pageNumber }),
          filterOpened, setFilterOpened,
          scrollY: state.scrollY, setScrollY: (scrollY: number) => changeState({ scrollY }),
          loading, setLoading,
          errors: lastError, setErrors: setLastError,
          orderBy: state.orderBy, setOrderBy: (orderBy: GlobalSearchOrderBy) => changeState({ orderBy: orderBy }),
          kFilters: state.kFilters, setKnowledgeFilters: (filters: Partial<KnowledgeSearchFilters>) => changeState({ pageNumber: 1, kFilters: { ...state.kFilters, ...filters } }),
          vFilters: state.vFilters, setVideoFilters: (filters: Partial<KnowledgeSearchFilters>) => changeState({ pageNumber: 1, vFilters: { ...state.vFilters, ...filters } }),
          dFilters: state.dFilters, setDocumentFilters: (filters: Partial<KnowledgeSearchFilters>) => changeState({ pageNumber: 1, dFilters: { ...state.dFilters, ...filters } }),
          tFilters: state.tFilters, setTrainingFilters: (filters: Partial<TrainingSearchFilters>) => changeState({ pageNumber: 1, tFilters: { ...state.tFilters, ...filters } }),
          searchResult
        } as GlobalSearchContextType
      }
    >
      <MprcInfiniteLoading visible={loading || loadingResume} />
      {
        lastError
          ? <MprcRequestErrorReport error={lastError} />
          : (
            <>
              {
                !loadingResume && !hasData.current &&
                <MprcFilterEmptyDataset message={t("GlobalSearch.EmptySearchMessage")} />
              }

              {children}
            </>
          )
      }

    </GlobalSearchContext.Provider>
  );
};

/////////////////////////////////////////////////////////////////////////////////////////////////
